'use client';
import { useRef } from 'react';
import { Context } from '@absmartly/javascript-sdk';
import {
  ContextData,
  ExperimentData,
} from '@absmartly/javascript-sdk/types/context';
import { PropsWithChildren, createContext, useContext, useEffect } from 'react';
import { getAbSmartlySDK } from 'src/lib/absmartly/sdk';
import { getDeviceIdWithoutHmac } from 'src/utils/get-device-id';
import { setupForcedVariants } from 'src/lib/absmartly/utils/setup-forced-variants';
import { getEventLogger } from 'src/lib/absmartly/utils/get-event-logger';
import { getVariationName } from 'src/lib/absmartly/utils/get-variation-key';

interface AbSmartlyProviderProps {
  contextData: ContextData;
  experimentMap: Map<string, ExperimentData>;
}

export type AbSmartlyProviderContext = {
  context: Context;
  experimentMap: Map<string, ExperimentData>;
};

export const AbSmartlyContext = createContext<AbSmartlyProviderContext | null>(
  null
);

export const AbSmartlyProvider = ({
  contextData,
  experimentMap,
  children,
}: PropsWithChildren<AbSmartlyProviderProps>) => {
  const { current: deviceId } = useRef(getDeviceIdWithoutHmac());

  const clientSideSdk = getAbSmartlySDK({
    eventLogger: getEventLogger({ deviceId, experimentMap }),
  });
  const clientSideContext = clientSideSdk.createContextWith(
    { units: { device_id: deviceId } },
    contextData
  );

  useEffect(() => {
    clientSideContext.attributes({
      page_url: window.location.href,
    });
    setupForcedVariants({ context: clientSideContext, experimentMap });
  }, [clientSideContext, experimentMap]);

  return (
    <AbSmartlyContext.Provider
      value={{ context: clientSideContext, experimentMap }}
    >
      {children}
    </AbSmartlyContext.Provider>
  );
};

export const useAbSmartlyExperiments = (
  experimentNames: string[]
): string[] => {
  const providerContext = useContext(AbSmartlyContext);
  if (!providerContext) {
    throw new Error('Missing <ABSmartlyProvider />');
  }
  const { context, experimentMap } = providerContext;
  return experimentNames.map((experimentName) => {
    const experiment = experimentMap.get(experimentName);
    const variantIndex = context.treatment(experimentName);
    return getVariationName({ experiment, variantIndex });
  });
};
